import React from 'react';

import { Translate } from 'react-localize-redux';
import { Route } from 'react-router';

import LazyComponent from '@fd/ui/LazyComponent';

import { toKebabCase } from '../../helpers/strings';
import { lazyWithRetry } from '../../helpers/utilities';
import { RMSModules } from '../RMS/utils/rmsModules';
import { RMSSubscriptions } from '../RMS/utils/rmsSubscriptions';
import POSPage from './POS/POSPage';

const RMSPage = lazyWithRetry(() => import('../RMS/components/RMSPage'));

type Props = {
  path: string;
  appId?: string;
};

const PointOfSaleRoutes: React.FC<Props> = ({ path, appId }) => {
  const toPosSettingsPath = `/${appId}/sales-channels/point-of-sale?tab=settings`;

  return (
    <>
      {/* Point of Sale Home */}
      <Route exact path={`${path}/point-of-sale`} component={POSPage} />

      {/* Network Diagram */}
      <Route
        exact
        path={`${path}/point-of-sale/network-diagram`}
        render={() => (
          <LazyComponent>
            <RMSPage
              subscription={RMSSubscriptions.BASE}
              title={<Translate id="Network_Diagram" />}
              module={RMSModules.NETWORK_DIAGRAM}
              url={`configurations/system/${RMSModules.NETWORK_DIAGRAM}`}
              includePropertyIdInUrl
              toParent={toPosSettingsPath}
            />
          </LazyComponent>
        )}
      />

      {/* Guests */}
      <Route
        exact
        path={`${path}/point-of-sale/${RMSModules.GUESTS}`}
        render={() => (
          <LazyComponent>
            <RMSPage
              subscription={RMSSubscriptions.BASE}
              title={<Translate id="Guests" />}
              module={RMSModules.GUESTS}
              url={`configurations/system/${RMSModules.GUESTS}`}
              toParent={toPosSettingsPath}
            />
          </LazyComponent>
        )}
      />

      {/* Print Configuration */}
      <Route
        exact
        path={`${path}/point-of-sale/${toKebabCase(RMSModules.PRINT_CONFIGURATION)}`}
        render={() => (
          <LazyComponent>
            <RMSPage
              subscription={RMSSubscriptions.BASE}
              title={<Translate id="Printer" />}
              module={RMSModules.PRINT_CONFIGURATION}
              url={`configurations/franchisors/properties/${RMSModules.PRINT_CONFIGURATION}`}
              includePropertyIdInUrl
              toParent={toPosSettingsPath}
            />
          </LazyComponent>
        )}
      />

      {/* Tags */}
      <Route
        exact
        path={`${path}/point-of-sale/${RMSModules.TAGS}`}
        render={() => (
          <LazyComponent>
            <RMSPage
              subscription={RMSSubscriptions.BASE}
              title={<Translate id="Tags" />}
              module={RMSModules.TAGS}
              url={`configurations/system/${RMSModules.TAGS}`}
              toParent={toPosSettingsPath}
            />
          </LazyComponent>
        )}
      />

      {/* Price Bands */}
      <Route
        exact
        path={`${path}/point-of-sale/${toKebabCase(RMSModules.PRICE_BANDS)}`}
        render={() => (
          <LazyComponent>
            <RMSPage
              subscription={RMSSubscriptions.BASE}
              title={<Translate id="Price_Bands" />}
              module={RMSModules.PRICE_BANDS}
              url={`configurations/system/${RMSModules.PRICE_BANDS}`}
              toParent={toPosSettingsPath}
            />
          </LazyComponent>
        )}
      />

      {/* Payment Methods */}
      <Route
        exact
        path={`${path}/point-of-sale/payment-methods`}
        render={() => (
          <LazyComponent>
            <RMSPage
              subscription={RMSSubscriptions.BASE}
              title={<Translate id="Payment_Types" />}
              module={RMSModules.PAYMENT_TYPES}
              url={`configurations/system/${RMSModules.PAYMENT_TYPES}`}
              toParent={toPosSettingsPath}
            />
          </LazyComponent>
        )}
      />

      {/* Freebie Reasons */}
      <Route
        exact
        path={`${path}/point-of-sale/${toKebabCase(RMSModules.FREEBIE_REASONS)}`}
        render={() => (
          <LazyComponent>
            <RMSPage
              subscription={RMSSubscriptions.BASE}
              title={<Translate id="Freebie_Reasons" />}
              module={RMSModules.FREEBIE_REASONS}
              url={`configurations/system/${RMSModules.FREEBIE_REASONS}`}
              toParent={toPosSettingsPath}
            />
          </LazyComponent>
        )}
      />
    </>
  );
};

export default PointOfSaleRoutes;
